import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Star,
  ChevronLeft,
  ChevronRight,
  Loader,
  Package,
  ShoppingCart,
  Heart,
  Plus,
} from "lucide-react";

const Product = ({ product, variant = "full" }) => {

  console.log("Rendering Product component with product:", product);
  const navigate = useNavigate();
  const [selectedWeight, setSelectedWeight] = useState("");
  const [originalPrice, setOriginalPrice] = useState(0);
  const [sellingPrice, setSellingPrice] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isWishlisted, setIsWishlisted] = useState(false);

  useEffect(() => {
    setCurrentImageIndex(0);

    // Handle single weight/price structure (from API)
    if (product?.weight && product?.price) {
      setSelectedWeight(product.weight);
      // Set original price from the product
      setOriginalPrice(product.price);
      // Check if discounted amount exists for this product
      if (product?.discountedAmount && Number(product.discountedAmount) > 0) {
        setSellingPrice(Number(product.discountedAmount));
        // Calculate discount percentage
        if (product.price > 0) {
          const discountPercent = Math.round(
            ((product.price - Number(product.discountedAmount)) /
              product.price) *
              100
          );
          setDiscountPercentage(discountPercent);
        }
      } else {
        setSellingPrice(product.price);
        setDiscountPercentage(0);
      }
    } else {
      setSelectedWeight("");
      setOriginalPrice(0);
      setSellingPrice(0);
      setDiscountPercentage(0);
    }
  }, [product]);

  // Functions to handle image navigation
  const nextImage = (e) => {
    e.stopPropagation();
    if (product.productImages && product.productImages.length > 0) {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === product.productImages.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (product.productImages && product.productImages.length > 0) {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === 0 ? product.productImages.length - 1 : prevIndex - 1
      );
    }
  };

  const handleProductDetails = (product) => {
    navigate(`/product/${product._id}`, {
      state: {
        item: product,
        productId: product._id,
      },
    });
  };

  const handleWishlistToggle = (e) => {
    e.stopPropagation();
    setIsWishlisted(!isWishlisted);
    // Add your wishlist logic here
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    // Add your cart logic here
    console.log("Added to cart:", product);
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return "bg-green-500";
    if (rating >= 3) return "bg-yellow-500";
    return "bg-red-500";
  };

  if (!product) {
    return <div>Loading...</div>;
  }

  // Get current product image or default
  const currentImage =
    product.productImages && product.productImages.length > 0
      ? product.productImages[currentImageIndex]
      : "No image available";

  // Check if we have multiple images
  const hasMultipleImages =
    product.productImages && product.productImages.length > 1;

  // Minimal variant for New Arrivals & Best Seller
  if (variant === "minimal") {
    return (
      <div className="group relative bg-white  overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
        {/* Wishlist Heart Icon */}
        {/* <button
          onClick={handleWishlistToggle}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white transition-all duration-200"
        >
          <Heart
            size={16}
            className={`transition-all duration-200 ${
              isWishlisted
                ? "fill-red-500 text-red-500"
                : "text-gray-400 hover:text-red-400"
            }`}
          />
        </button> */}

        <div
          className="cursor-pointer"
          onClick={() => handleProductDetails(product)}
        >
          {/* Image Section */}
          <div className="relative bg-gray-50 aspect-square overflow-hidden">
            <img
              src={currentImage}
              alt={product.productName || "Product"}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />

            {/* Discount Badge */}
            {discountPercentage > 0 && (
              <div className="absolute top-3 left-3 bg-red-500 text-white px-2 py-1 rounded-md text-xs font-medium">
                {discountPercentage}% OFF
              </div>
            )}

            {/* Out of Stock Overlay */}
            {product.isOutOfStock && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <span className="bg-white px-3 py-1.5 rounded-lg text-sm font-semibold text-gray-800">
                  Out of Stock
                </span>
              </div>
            )}

            {/* Image Navigation - only show if multiple images */}
            {hasMultipleImages && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-white"
                >
                  <ChevronLeft size={14} className="text-gray-600" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-white"
                >
                  <ChevronRight size={14} className="text-gray-600" />
                </button>
              </>
            )}
          </div>

          {/* Content Section - Minimal */}
          <div className="p-3 space-y-2">
            {/* Product Name */}
            <h3 className="font-medium text-gray-800 text-sm leading-tight line-clamp-2 min-h-[2.5rem]">
              {product.productName || "Product Name"}
            </h3>

            {/* Price Section */}
            <div className="space-y-1">
              {discountPercentage > 0 ? (
                <div className="flex items-baseline gap-1.5 flex-wrap">
                  <span className="text-lg  text-gray-500">
                    ₹{sellingPrice?.toLocaleString()}
                  </span>
                  <span className="text-xs text-gray-500 line-through">
                    ₹{originalPrice?.toLocaleString()}
                  </span>
                </div>
              ) : (
                <span className="text-lg  text-gray">
                  ₹{sellingPrice?.toLocaleString()}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Full variant for All Products page
  return (
    <div className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
      {/* Wishlist Heart Icon */}
      <button
        onClick={handleWishlistToggle}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white transition-all duration-200"
      >
        <Heart
          size={18}
          className={`transition-all duration-200 ${
            isWishlisted
              ? "fill-red-500 text-red-500"
              : "text-gray-400 hover:text-red-400"
          }`}
        />
      </button>

      <div
        className="cursor-pointer"
        onClick={() => handleProductDetails(product)}
      >
        {/* Image Section */}
        <div className="relative bg-gray-50 aspect-square overflow-hidden">
          <img
            src={currentImage}
            alt={product.productName || "Product"}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />

          {/* Discount Badge */}
          {discountPercentage > 0 && (
            <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium">
              {discountPercentage}% OFF
            </div>
          )}

          {/* Out of Stock Overlay */}
          {product.isOutOfStock && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <span className="bg-white px-4 py-2 rounded-lg font-semibold text-gray-800">
                Out of Stock
              </span>
            </div>
          )}

          {/* Image Navigation - only show if multiple images */}
          {hasMultipleImages && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-white"
              >
                <ChevronLeft size={16} className="text-gray-600" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-white"
              >
                <ChevronRight size={16} className="text-gray-600" />
              </button>
            </>
          )}

          {/* Image Dots Indicator */}
          {hasMultipleImages && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {product.productImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(idx);
                  }}
                  className={`w-2 h-2 rounded-full transition-all duration-200 ${
                    idx === currentImageIndex
                      ? "bg-white scale-110"
                      : "bg-white/50 hover:bg-white/80"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="p-4 space-y-3">
          {/* Product Name */}
          <h3 className="font-semibold text-gray-800 text-lg leading-tight line-clamp-2 min-h-[3.5rem]">
            {product.productName || "Product Name"}
          </h3>

          {/* Price Section */}
          <div className="space-y-1">
            {discountPercentage > 0 ? (
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-xl font-bold text-gray-900">
                  ₹{sellingPrice?.toLocaleString()}
                </span>
                <span className="text-sm text-gray-500 line-through">
                  ₹{originalPrice?.toLocaleString()}
                </span>
              </div>
            ) : (
              <span className="text-xl font-bold text-gray-900">
                ₹{sellingPrice?.toLocaleString()}
              </span>
            )}
            
            {/* Net Quantity */}
            {selectedWeight && (
              <p className="text-sm text-gray-600">
                Net Qty: {selectedWeight}
              </p>
            )}
          </div>

          {/* Rating */}
          {product.avgRating > 0 && (
            <div className="flex items-center gap-2">
              <div
                className={`px-2 py-1 rounded text-xs font-medium flex items-center text-white ${getRatingColor(
                  product.avgRating
                )}`}
              >
                {product.avgRating.toFixed(1)}
                <Star size={12} className="ml-1 fill-current" />
              </div>
              <span className="text-xs text-gray-500">
                ({product.ratings?.length || 0} reviews)
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons - Full Version */}
      <div className="p-4 pt-0 space-y-2">
        {/* Add to Cart Button */}
        <button
          onClick={handleAddToCart}
          className={`w-full py-2.5 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
            product.isOutOfStock
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md"
          }`}
          disabled={product.isOutOfStock}
        >
          {product.isOutOfStock ? (
            <>
              <Package size={16} />
              <span>Out of Stock</span>
            </>
          ) : (
            <>
              <Plus size={16} />
              <span>Add to Cart</span>
            </>
          )}
        </button>

        {/* View Product Button */}
        <button
          onClick={() => handleProductDetails(product)}
          className="w-full py-2.5 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white shadow-sm hover:shadow-md"
        >
          <ShoppingCart size={16} />
          <span>View Product</span>
        </button>
      </div>
    </div>
  );
};

export default Product;